import { useState } from 'react';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import { useBizState } from '@/hooks/use-biz-state';
import { useCore } from '@/hooks/use-core';
import { useCoreRequest } from '@/hooks/use-core-request';
import { getDevice } from '@/utils/browser';

type ErrorType = {
  title?: string;
  message: string;
} | null;

export function useEnableFido() {
  const { areaCode, authType, userName, supportPassKey, setBizState } = useBizState();

  const { lenovoId, setFlow } = useCore();
  const [error, setError] = useState<ErrorType>(null);

  // run enable passkey
  const { run, loading } = useCoreRequest(
    async () => {
      setError(null);
      return await lenovoId.fidoCreate({
        areaCode: authType === 'phone' ? areaCode : '',
        userName,
        displayName: getDevice(),
      });
    },
    {
      ready: supportPassKey,
      manual: true,
      onSuccess({ data }) {
        setBizState(data);
        if (Flow.LOGIN_STEP_SUCCESS_CALLBACK && data?.callBack) {
          window.location.href = data.callBack;
        } else {
          setFlow(Flow.EXCEPTION_SOMETHING_WENT_WRONG);
        }
      },
      onError: (code) => {
        switch (code) {
          case ErrorCode.UserCancelledFido:
            return true;
          case ErrorCode.SessionExpired:
            setError({
              title: 'session_expired',
              message: 'session_expired_description',
            });
            return true;
          case ErrorCode.FidoCreateFailed:
            setError({
              message: 'fido_create_failed',
            });
            return true;
          case ErrorCode.PasskeyException:
            setError({
              title: 'passkey_exception',
              message: 'passkey_exception_description',
            });
            return true;
          case ErrorCode.FidoDataAccessError:
          case ErrorCode.PasskeyHttpsRequired:
            setError({
              message: 'unable_enable_passkey',
            });
            return true;
        }
      },
    },
  );

  return {
    run,
    loading,
    error,
  };
}

export function useLoginWithFido() {
  const { areaCode, authType, userName, supportPassKey, autoLoginState, setBizState } = useBizState();
  const { lenovoId, setFlow } = useCore();
  const [error, setError] = useState<ErrorType>(null);

  // run enable passkey
  const { run, loading } = useCoreRequest(
    async () => {
      setError(null);
      return await lenovoId.fidoAuth({
        areaCode: authType === 'phone' ? areaCode : '',
        userName,
        autoLoginState: autoLoginState ? '1' : '0',
      });
    },
    {
      ready: supportPassKey,
      manual: true,
      onSuccess({ data }) {
        setBizState(data);
        if (Flow.LOGIN_STEP_SUCCESS_CALLBACK && data?.callBack) {
          window.location.href = data.callBack;
        } else {
          setFlow(Flow.EXCEPTION_SOMETHING_WENT_WRONG);
        }
      },
      onError: (code) => {
        switch (code) {
          case ErrorCode.UserCancelledFido:
            return true;
          case ErrorCode.SessionExpired:
            setError({
              title: 'session_expired',
              message: 'session_expired_description',
            });
            return true;
          case ErrorCode.FidoAuthFailed:
            setError({
              message: 'fido_auth_failed',
            });
            return true;
          case ErrorCode.PasskeyException:
            setError({
              title: 'passkey_exception',
              message: 'passkey_exception_description',
            });
            return true;
          case ErrorCode.FidoDataAccessError:
          case ErrorCode.PasskeyHttpsRequired:
            setError({
              message: 'unable_enable_passkey',
            });
            return true;
        }
      },
    },
  );

  return {
    run,
    loading,
    error,
  };
}
